import type {
  AnyPlayActRequest,
  AnyPlayActResult,
  AnyPlayView,
  PlayActRequest,
  PlayActResult,
  PlayConflict,
  PlayView,
} from '@goodtime/shared';
import { api, ApiError } from './client';

export function getPlay(playId: string, sessionId: string): Promise<PlayView> {
  return api<PlayView>(`/plays/${encodeURIComponent(playId)}?sessionId=${encodeURIComponent(sessionId)}`);
}

/** One move in a play. The key is the same on every retry of this step. */
export function actPlay(playId: string, req: PlayActRequest): Promise<PlayActResult> {
  return api<PlayActResult>(`/plays/${encodeURIComponent(playId)}/act`, {
    method: 'POST',
    headers: { 'Idempotency-Key': `${playId}-a${req.step}` },
    body: JSON.stringify(req),
  });
}

export function declinePlay(playId: string, sessionId: string): Promise<PlayView> {
  return api<PlayView>(`/plays/${encodeURIComponent(playId)}/decline`, {
    method: 'POST',
    headers: { 'Idempotency-Key': `${playId}-decline` },
    body: JSON.stringify({ sessionId }),
  });
}

/** The play in a 409 (`PLAY_CONFLICT`), or null. */
export function playFromConflict(err: unknown): PlayView | null {
  if (!(err instanceof ApiError) || err.status !== 409) return null;
  try {
    return (JSON.parse(err.body) as Partial<PlayConflict>).play ?? null;
  } catch {
    return null;
  }
}

/** Any game's play now (the road's mini-games; Hi-Lo uses `getPlay`). */
export function fetchPlay<V extends AnyPlayView>(playId: string, sessionId: string): Promise<V> {
  return api<V>(`/plays/${encodeURIComponent(playId)}?sessionId=${encodeURIComponent(sessionId)}`);
}

/** One move in any game. The key is the same on every retry of this step,
 *  so a retried move is answered once. */
export function playAct<R extends AnyPlayActResult>(playId: string, req: AnyPlayActRequest): Promise<R> {
  return api<R>(`/plays/${encodeURIComponent(playId)}/act`, {
    method: 'POST',
    headers: { 'Idempotency-Key': `${playId}-a${req.step}` },
    body: JSON.stringify(req),
  });
}

/** Any game's play in a 409 (`PLAY_CONFLICT`), or null. */
export function anyPlayFromConflict<V extends AnyPlayView>(err: unknown): V | null {
  return playFromConflict(err) as unknown as V | null;
}
